import * as THREE from 'three';
import earcut from 'earcut';
import Module from 'manifold-3d';
import type { ManifoldToplevel, Manifold as ManifoldT } from 'manifold-3d';
// Resolve the WASM binary through Vite so it's served as a bundled asset.
// Without this, Emscripten resolves `manifold.wasm` relative to the JS module
// inside node_modules, which the dev server blocks (403 via the `@fs/` guard)
// and which wouldn't be emitted in a production build.
import manifoldWasmUrl from 'manifold-3d/manifold.wasm?url';

/**
 * Thin wrapper around the Manifold WASM library for the floor-mesh boolean
 * subtraction (cutting door/window openings out of walls).
 *
 * Why Manifold and not three-bvh-csg: three-bvh-csg's subtraction is not
 * guaranteed two-manifold — it splits triangles along the cut contour and the
 * split vertices don't line up across adjacent faces, leaving loose/stray
 * triangles and T-junctions along every opening. Manifold's boolean is
 * watertight-by-construction, so the output is a clean, indexed, two-manifold
 * mesh. See FloorMesh3DBuilder for how it's used.
 *
 * The WASM module loads asynchronously, but the floor build path is
 * synchronous (it runs inside a requestAnimationFrame). We therefore kick off
 * {@link initManifold} eagerly at app startup and expose a synchronous
 * {@link subtractMeshes} that returns null until the module is ready (callers
 * fall back to the uncut geometry; the floor's rAF rebuild retries once the
 * module resolves).
 */

let toplevel: ManifoldToplevel | null = null;
let initPromise: Promise<ManifoldToplevel> | null = null;
/** Callbacks fired once, when the WASM module finishes loading. */
const readyCallbacks = new Set<() => void>();

/**
 * Run `cb` when Manifold is ready. Fires immediately (synchronously) if the
 * module is already loaded, otherwise once init resolves. Returns an
 * unsubscribe function. Floors use this to rebuild themselves if they were
 * first built (uncut) before the WASM was available.
 */
export function onManifoldReady(cb: () => void): () => void {
  if (toplevel) {
    cb();
    return () => undefined;
  }
  readyCallbacks.add(cb);
  return () => readyCallbacks.delete(cb);
}

/**
 * Begin loading the Manifold WASM module. Idempotent — safe to call multiple
 * times; subsequent calls return the same promise. Call once at startup so the
 * module is ready before the first floor with openings is built.
 */
export function initManifold(): Promise<ManifoldToplevel> {
  if (initPromise) return initPromise;
  // Point Emscripten at the Vite-resolved asset URL instead of letting it guess
  // a node_modules-relative path.
  initPromise = Module({ locateFile: () => manifoldWasmUrl })
    .then((wasm) => {
      wasm.setup();
      toplevel = wasm;
      // Notify waiters (e.g. floors built uncut before WASM was ready) so they
      // can rebuild now that the boolean is available. Isolate each callback —
      // a throw in one (e.g. a CSG rebuild) must not reject the init promise or
      // skip the others.
      readyCallbacks.forEach((cb) => {
        try {
          cb();
        } catch (err) {
          console.error('Manifold ready callback failed:', err);
        }
      });
      readyCallbacks.clear();
      return wasm;
    })
    .catch((err) => {
      // Reset so a later call can retry rather than being stuck on a rejected
      // promise.
      initPromise = null;
      console.error('Manifold WASM failed to initialize:', err);
      throw err;
    });
  return initPromise;
}

/** True once the WASM module is loaded and {@link subtractMeshes} can run. */
export function isManifoldReady(): boolean {
  return toplevel !== null;
}

/** A contiguous run of triangles in the base geometry that shares one string
 * tag (e.g. a shell piece or a room id). `start`/`count` index the triangle
 * list (i.e. index-buffer offsets divided by 3 × 3 — see usage). */
export interface TagRange {
  /** First *index* (not triangle) of the run in the base index buffer. */
  start: number;
  /** Number of *indices* in the run (always a multiple of 3). */
  count: number;
  /** Provenance tag carried through the boolean (shell piece or room id). */
  tag: string;
}

/** A cutter geometry plus the tag (opening id) to attribute its cut faces to. */
export interface TaggedCutter {
  geometry: THREE.BufferGeometry;
  tag: string;
}

/** Result of a tagged subtraction: the watertight geometry plus a parallel
 * map from each `geometry.groups` entry's materialIndex to its source tag. */
export interface SubtractResult {
  geometry: THREE.BufferGeometry;
  /** `groupTags[materialIndex]` is the tag (shell piece / room id / opening
   * id) that produced that group's triangles. */
  groupTags: string[];
}

/**
 * Subtract `cutters` from `base` and return the resulting watertight,
 * **indexed** position-only geometry — with per-triangle provenance preserved.
 *
 * Each base triangle run (`baseRanges`) and each cutter (`cutters`) is tagged
 * with a string id. Manifold carries those through the boolean via run
 * original-IDs: original wall/room faces keep their tag, and the reveal faces
 * a cutter creates come back tagged with that opening's id. We read the runs
 * back out and rebuild `geometry.groups` + a parallel `groupTags` map so the
 * caller can trace every triangle to its room/shell/opening.
 *
 * Returns null when the WASM module isn't ready yet — callers should fall back
 * to the uncut `base`. All inputs are treated as position-only (numProp = 3).
 */
export function subtractMeshes(base: THREE.BufferGeometry, baseRanges: TagRange[], cutters: TaggedCutter[]): SubtractResult | null {
  const wasm = toplevel;
  if (!wasm) return null;

  // Reserve a unique numeric Manifold id for every distinct tag. Tags may
  // repeat across base ranges and cutters; each distinct tag gets one id.
  const tagToId = new Map<string, number>();
  const idToTag = new Map<number, string>();
  const internTag = (tag: string): number => {
    let id = tagToId.get(tag);
    if (id === undefined) {
      id = wasm.Manifold.reserveIDs(1);
      tagToId.set(tag, id);
      idToTag.set(id, tag);
    }
    return id;
  };

  // Build base + cutter manifolds AND do the boolean inside one try/catch:
  // `new wasm.Manifold(mesh)` throws a ManifoldError on a non-2-manifold base,
  // and that must NEVER escape (callers fall back to the uncut geometry; an
  // uncaught throw here would propagate through onManifoldReady into the WASM
  // init promise and crash the app).
  let baseManifold: ManifoldT | null = null;
  let cutterManifolds: ManifoldT[] = [];
  try {
    baseManifold = baseGeometryToManifold(wasm, base, baseRanges, internTag);
    if (!baseManifold) return null;

    cutterManifolds = cutters.map((c) => geometryToManifold(wasm, c.geometry, internTag(c.tag))).filter((m): m is ManifoldT => m !== null);

    let result: ManifoldT;
    if (cutterManifolds.length === 0) {
      result = baseManifold;
    } else {
      // Difference of the whole list in one call is both faster and more
      // robust than chaining pairwise subtractions.
      result = wasm.Manifold.difference([baseManifold, ...cutterManifolds]);
    }
    const out = manifoldToGeometry(result, idToTag);
    // Free WASM-side memory. `result` may alias `baseManifold` when there were
    // no cutters, so guard against a double free.
    if (result !== baseManifold) result.delete();
    baseManifold.delete();
    cutterManifolds.forEach((m) => m.delete());
    return out;
  } catch (err) {
    console.error('Manifold subtraction failed:', err);
    baseManifold?.delete();
    cutterManifolds.forEach((m) => m.delete());
    return null;
  }
}

/** One extruded wall region: a closed 2D ring extruded between [bottom, top],
 * with optional convex sub-polygons carved out of it (`subtract` — the node
 * kernel cells a wall rectangle overlaps at each end). */
export interface WallRegionInput {
  ring: { x: number; y: number }[];
  bottom: number;
  top: number;
  subtract?: { x: number; y: number }[][];
}

/**
 * Build the wall solid by UNIONING a per-region extrusion of each {@link
 * WallRegionInput}, then subtract `cutters`. Unlike {@link subtractMeshes} this
 * does NOT require the regions to tile into a single 2-manifold: Manifold's
 * union is robust to overlapping/abutting solids, so it tolerates the small
 * cap overlaps the wall partition produces at shallow/near-collinear junctions
 * (which would otherwise make a stitched base non-manifold and silently skip the
 * cut). The whole wall mass is tagged `__wall__`; cutter reveal faces keep their
 * opening tag. Returns null when the WASM module isn't ready.
 */
export function subtractFromRegions(regions: WallRegionInput[], cutters: TaggedCutter[]): SubtractResult | null {
  const wasm = toplevel;
  if (!wasm) return null;

  const tagToId = new Map<string, number>();
  const idToTag = new Map<number, string>();
  const internTag = (tag: string): number => {
    let id = tagToId.get(tag);
    if (id === undefined) {
      id = wasm.Manifold.reserveIDs(1);
      tagToId.set(tag, id);
      idToTag.set(id, tag);
    }
    return id;
  };
  const wallId = internTag('__wall__');

  const built: ManifoldT[] = [];
  let cutterManifolds: ManifoldT[] = [];
  try {
    for (const region of regions) {
      const m = regionToManifold(wasm, region, wallId);
      if (m) built.push(m);
    }
    if (built.length === 0) return null;

    // Union all region solids into one watertight wall mass.
    const wall = wasm.Manifold.union(built);

    cutterManifolds = cutters.map((c) => geometryToManifold(wasm, c.geometry, internTag(c.tag))).filter((m): m is ManifoldT => m !== null);
    let result = wall;
    if (cutterManifolds.length > 0) {
      result = wasm.Manifold.difference([wall, ...cutterManifolds]);
    }
    const out = manifoldToGeometry(result, idToTag);
    if (result !== wall) result.delete();
    wall.delete();
    built.forEach((m) => m.delete());
    cutterManifolds.forEach((m) => m.delete());
    return out;
  } catch (err) {
    console.error('Manifold wall union/subtraction failed:', err);
    built.forEach((m) => m.delete());
    cutterManifolds.forEach((m) => m.delete());
    return null;
  }
}

/** A footprint polygon: one CCW outer contour with zero or more CW hole
 * contours, ready for earcut. Coordinates are `[x, y]`. */
export interface FootprintPolygon {
  outer: [number, number][];
  holes: [number, number][][];
}

/**
 * The flat 2D footprint of the wall partition: for each region, `ring` with its
 * `subtract` cells carved out, all unioned — using Manifold's 2D CrossSection
 * boolean, which is ROBUST to the exact-coincident rect/cell edges that make
 * polygon-clipping throw. Returns grouped outer+hole contours (the wall
 * silhouette + interior cavity holes). Returns null when the WASM module isn't
 * ready (callers fall back to a simple per-region fill until it loads).
 */
export function regionsToFootprint2D(regions: WallRegionInput[], cutters?: { x: number; y: number }[][]): FootprintPolygon[] | null {
  const wasm = toplevel;
  if (!wasm) return null;
  const { CrossSection } = wasm;

  const toContour = (ring: { x: number; y: number }[]): [number, number][] => ring.map((p) => [p.x, p.y] as [number, number]);

  const pieces: InstanceType<typeof CrossSection>[] = [];
  try {
    for (const region of regions) {
      if (region.ring.length < 3) continue;
      let cs = CrossSection.ofPolygons([toContour(region.ring)], 'NonZero');
      if (region.subtract) {
        for (const cell of region.subtract) {
          if (cell.length < 3) continue;
          const cut = CrossSection.ofPolygons([toContour(cell)], 'NonZero');
          const diff = CrossSection.difference(cs, cut);
          cs.delete();
          cut.delete();
          cs = diff;
        }
      }
      pieces.push(cs);
    }
    if (pieces.length === 0) return [];

    let foot = pieces[0];
    for (let i = 1; i < pieces.length; i++) {
      const u = CrossSection.union(foot, pieces[i]);
      if (foot !== pieces[0]) foot.delete();
      foot = u;
    }

    // Punch door/window openings out of the unioned footprint. Each cutter is a
    // 2D rectangle straddling the wall (width = opening, depth = wall thickness
    // + overshoot), so the wall silhouette breaks cleanly at every opening —
    // the 2D analogue of the 3D prism subtraction.
    if (cutters) {
      const ownedByFirstPiece = () => foot === pieces[0];
      for (const rect of cutters) {
        if (rect.length < 3) continue;
        const cut = CrossSection.ofPolygons([toContour(rect)], 'NonZero');
        const diff = CrossSection.difference(foot, cut);
        cut.delete();
        if (!ownedByFirstPiece()) foot.delete();
        foot = diff;
      }
    }

    const contours = foot.toPolygons(); // Vec2[][] — CCW outers, CW holes
    if (foot !== pieces[0]) foot.delete();
    pieces.forEach((p) => p.delete());

    return groupContours(contours);
  } catch (err) {
    console.error('Manifold 2D footprint failed:', err);
    pieces.forEach((p) => {
      try {
        p.delete();
      } catch {
        /* already freed */
      }
    });
    return null;
  }
}

/**
 * `polygon` minus every `cutter` ring, via Manifold's robust 2D boolean —
 * a room's SKINNED interior: the room polygon minus the floor's
 * wall-partition rings leaves exactly the interior the skinned walls bound.
 * Returns grouped outer+holes polygons, or null while the WASM module isn't
 * loaded (callers fall back to the plain polygon).
 */
export function polygonMinusRegions(polygon: { x: number; y: number }[], cutters: { x: number; y: number }[][]): FootprintPolygon[] | null {
  const wasm = toplevel;
  if (!wasm || polygon.length < 3) return null;
  const { CrossSection } = wasm;
  const toContour = (ring: { x: number; y: number }[]): [number, number][] => ring.map((p) => [p.x, p.y] as [number, number]);
  try {
    let cs = CrossSection.ofPolygons([toContour(polygon)], 'NonZero');
    for (const ring of cutters) {
      if (ring.length < 3) continue;
      const cut = CrossSection.ofPolygons([toContour(ring)], 'NonZero');
      const diff = CrossSection.difference(cs, cut);
      cs.delete();
      cut.delete();
      cs = diff;
    }
    const contours = cs.toPolygons() as [number, number][][];
    cs.delete();
    return groupContours(contours);
  } catch (err) {
    console.error('Manifold interior computation failed:', err);
    return null;
  }
}

/** Area of {@link polygonMinusRegions}'s result; null while WASM isn't loaded. */
export function polygonAreaMinusRegions(polygon: { x: number; y: number }[], cutters: { x: number; y: number }[][]): number | null {
  const interior = polygonMinusRegions(polygon, cutters);
  if (interior === null) return null;
  const ringArea = (ring: [number, number][]): number => {
    let s = 0;
    for (let i = 0; i < ring.length; i++) {
      const a = ring[i];
      const b = ring[(i + 1) % ring.length];
      s += a[0] * b[1] - b[0] * a[1];
    }
    return Math.abs(s / 2);
  };
  let area = 0;
  for (const poly of interior) {
    area += ringArea(poly.outer);
    for (const hole of poly.holes) area -= ringArea(hole);
  }
  return area;
}

/** Group flat contours (CCW outers, CW holes) into outer+holes polygons by
 * area sign and point-in-polygon containment. */
function groupContours(contours: [number, number][][]): FootprintPolygon[] {
  const signedArea = (ring: [number, number][]) => {
    let s = 0;
    for (let i = 0; i < ring.length; i++) {
      const a = ring[i];
      const b = ring[(i + 1) % ring.length];
      s += a[0] * b[1] - b[0] * a[1];
    }
    return s / 2;
  };
  const inside = (pt: [number, number], ring: [number, number][]) => {
    let hit = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const a = ring[i];
      const b = ring[j];
      if (a[1] > pt[1] !== b[1] > pt[1] && pt[0] < ((b[0] - a[0]) * (pt[1] - a[1])) / (b[1] - a[1]) + a[0]) hit = !hit;
    }
    return hit;
  };
  const outers: FootprintPolygon[] = [];
  const holes: [number, number][][] = [];
  for (const c of contours) {
    if (c.length < 3) continue;
    if (signedArea(c) >= 0) outers.push({ outer: c, holes: [] });
    else holes.push(c);
  }
  // Sort outers smallest-first so a hole lands in its tightest containing outer.
  outers.sort((a, b) => Math.abs(signedArea(a.outer)) - Math.abs(signedArea(b.outer)));
  for (const hole of holes) {
    const ref = hole[0];
    const owner = outers.find((o) => inside(ref, o.outer));
    if (owner) owner.holes.push(hole);
  }
  return outers;
}

/** Extrude one region into a closed solid Manifold tagged `id`. The region's
 * `ring` is the prism; any `subtract` cells are carved out (each extruded a hair
 * taller in Z so the boolean is a clean through-cut). Returns null if degenerate. */
function regionToManifold(wasm: ManifoldToplevel, region: WallRegionInput, id: number): ManifoldT | null {
  const base = prismFromRing(wasm, region.ring, region.bottom, region.top, id);
  if (!base) return null;
  let solid: ManifoldT = base;
  if (region.subtract && region.subtract.length > 0) {
    for (const cell of region.subtract) {
      // Extrude the carve cell slightly beyond [bottom, top] so it cuts cleanly
      // through both caps (no coplanar-face ambiguity).
      const cutter = prismFromRing(wasm, cell, region.bottom - 1, region.top + 1, id);
      if (!cutter) continue;
      const prev = solid;
      solid = wasm.Manifold.difference([prev, cutter]);
      prev.delete();
      cutter.delete();
    }
  }
  return solid;
}

/** Build a closed prism Manifold from a 2D `ring` extruded between [bottom, top],
 * tagged `id`. Bottom cap faces −Z, top +Z; sides are outward quads.
 *
 * The ring is FIRST cleaned through `CrossSection.ofPolygons` with the NonZero
 * fill rule, which splits self-intersecting / bowtie rings into proper
 * non-overlapping sub-polygons. Each sub-polygon is then earcut-triangulated
 * and extruded. This lets the partition emit "raw" mitered polygons whose caps
 * project axially past each other on short connectors — Manifold cleans them
 * into the geometrically correct two-triangle shape before extrusion, so the
 * result is always a valid manifold prism.
 *
 * Null if the ring is fully degenerate (< 3 points or empty after cleanup). */
function prismFromRing(wasm: ManifoldToplevel, ring: { x: number; y: number }[], bottom: number, top: number, id: number): ManifoldT | null {
  if (ring.length < 3) return null;

  // Use CrossSection.NonZero to clean any self-intersections. For a normal
  // (well-wound, non-self-intersecting) ring this is essentially a no-op; for
  // a bowtie it splits the ring into the geometrically-enclosed sub-polygons.
  const { CrossSection } = wasm;
  const contour: [number, number][] = ring.map((p) => [p.x, p.y] as [number, number]);
  let cs: InstanceType<typeof CrossSection>;
  try {
    cs = CrossSection.ofPolygons([contour], 'NonZero');
  } catch {
    return null;
  }
  const cleanedPolys = cs.toPolygons();
  cs.delete();
  // Group cleaned contours into outer/holes by area sign + containment.
  const grouped = groupContours(cleanedPolys);
  if (grouped.length === 0) return null;

  // Concatenate all sub-polygons' triangulations into a single mesh.
  const vertProperties: number[] = [];
  const tris: number[] = [];
  for (const poly of grouped) {
    const flat: number[] = [];
    const holeIdx: number[] = [];
    for (const [x, y] of poly.outer) flat.push(x, y);
    for (const hole of poly.holes) {
      holeIdx.push(flat.length / 2);
      for (const [x, y] of hole) flat.push(x, y);
    }
    const capTris = earcut(flat, holeIdx.length > 0 ? holeIdx : undefined, 2);
    if (capTris.length === 0) continue;
    const n = flat.length / 2;
    const base = vertProperties.length / 3 / 2; // offset within combined mesh, in bottom-cap units
    for (let i = 0; i < n; i++) {
      vertProperties.push(flat[i * 2], flat[i * 2 + 1], bottom);
    }
    for (let i = 0; i < n; i++) {
      vertProperties.push(flat[i * 2], flat[i * 2 + 1], top);
    }
    // Bottom-cap triangles (CW from above = −Z normal), then top (CCW = +Z).
    const subBase = base * 2; // index of this sub-poly's bottom vertices in the combined array
    const topBase = subBase + n;
    for (let i = 0; i < capTris.length; i += 3) {
      const a = capTris[i];
      const b = capTris[i + 1];
      const c = capTris[i + 2];
      tris.push(subBase + a, subBase + c, subBase + b);
      tris.push(topBase + a, topBase + b, topBase + c);
    }
    // Vertical side quads connecting bottom ↔ top of EACH ring (outer + holes).
    let ringStart = 0;
    const ringSizes = [poly.outer.length, ...poly.holes.map((h) => h.length)];
    for (const rs of ringSizes) {
      for (let i = 0; i < rs; i++) {
        const a = ringStart + i;
        const b = ringStart + ((i + 1) % rs);
        tris.push(subBase + a, subBase + b, topBase + b);
        tris.push(subBase + a, topBase + b, topBase + a);
      }
      ringStart += rs;
    }
  }
  if (tris.length === 0) return null;

  const vertArr = new Float32Array(vertProperties);
  const triVerts = Uint32Array.from(tris);
  const mesh = new wasm.Mesh({
    numProp: 3,
    vertProperties: vertArr,
    triVerts,
    runIndex: new Uint32Array([0, triVerts.length]),
    runOriginalID: new Uint32Array([id]),
  });
  mesh.merge();
  return new wasm.Manifold(mesh);
}

/** Build the base Manifold, assigning one run per tag range so provenance
 * survives the boolean. Runs must be sorted by id and cover all triangles. */
function baseGeometryToManifold(
  wasm: ManifoldToplevel,
  geometry: THREE.BufferGeometry,
  ranges: TagRange[],
  internTag: (tag: string) => number,
): ManifoldT | null {
  const posAttr = geometry.getAttribute('position');
  if (!posAttr || posAttr.count === 0) return null;

  const src = posAttr.array;
  const vertProperties = src instanceof Float32Array ? src : new Float32Array(src);
  const triVerts = geometry.index
    ? new Uint32Array(geometry.index.array)
    : Uint32Array.from({ length: vertProperties.length / 3 }, (_, i) => i);

  // Manifold needs runs to cover triVerts in increasing index order; the same
  // id may appear in multiple, non-adjacent runs (e.g. one room bordering walls
  // in several places). So sort by start, NOT by id.
  const runs = ranges
    .filter((r) => r.count > 0)
    .map((r) => ({ start: r.start, end: r.start + r.count, id: internTag(r.tag) }))
    .sort((a, b) => a.start - b.start);

  let runIndex: Uint32Array | undefined;
  let runOriginalID: Uint32Array | undefined;
  if (runs.length > 0) {
    runIndex = new Uint32Array(runs.length + 1);
    runOriginalID = new Uint32Array(runs.length);
    for (let i = 0; i < runs.length; i++) {
      runIndex[i] = runs[i].start;
      runOriginalID[i] = runs[i].id;
    }
    runIndex[runs.length] = triVerts.length;
  }

  const mesh = new wasm.Mesh({ numProp: 3, vertProperties, triVerts, runIndex, runOriginalID });
  mesh.merge();
  return new wasm.Manifold(mesh);
}

/** Build a Manifold from a geometry's positions, as a single run tagged `id`
 * (used for cutters). Returns null if the geometry has no position data. */
function geometryToManifold(wasm: ManifoldToplevel, geometry: THREE.BufferGeometry, id: number): ManifoldT | null {
  const posAttr = geometry.getAttribute('position');
  if (!posAttr || posAttr.count === 0) return null;

  const src = posAttr.array;
  const vertProperties = src instanceof Float32Array ? src : new Float32Array(src);
  const triVerts = geometry.index
    ? new Uint32Array(geometry.index.array)
    : Uint32Array.from({ length: vertProperties.length / 3 }, (_, i) => i);

  const mesh = new wasm.Mesh({
    numProp: 3,
    vertProperties,
    triVerts,
    runIndex: new Uint32Array([0, triVerts.length]),
    runOriginalID: new Uint32Array([id]),
  });
  mesh.merge();
  return new wasm.Manifold(mesh);
}

/** Read a Manifold back into an indexed, position-only BufferGeometry, and
 * rebuild `geometry.groups` + a parallel `groupTags` map from its runs. */
function manifoldToGeometry(manifold: ManifoldT, idToTag: Map<number, string>): SubtractResult {
  const mesh = manifold.getMesh();
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(mesh.vertProperties, mesh.numProp));
  geometry.setIndex(new THREE.BufferAttribute(mesh.triVerts, 1));

  // Each run becomes a group; its materialIndex points into `groupTags`.
  const groupTags: string[] = [];
  for (let run = 0; run < mesh.numRun; run++) {
    const start = mesh.runIndex[run];
    const end = mesh.runIndex[run + 1];
    const tag = idToTag.get(mesh.runOriginalID[run]) ?? '__unknown__';
    geometry.addGroup(start, end - start, groupTags.length);
    groupTags.push(tag);
  }

  geometry.computeVertexNormals();
  return { geometry, groupTags };
}

